import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Save, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { LIDER_IA_COMPONENTS } from "@/data/liderIAEvaluationData";
import { useUpdateProject } from "@/hooks/useProjects";

interface Props {
  projectId: string;
  answers: Record<string, string>;
  readOnly?: boolean;
}

export function ProjectRubricAnswers({ projectId, answers, readOnly = false }: Props) {
  const updateProject = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>(answers || {});

  const answered = LIDER_IA_COMPONENTS.filter(c => (answers?.[c.id] || "").trim().length >= 20).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProject.mutateAsync({ id: projectId, rubric_answers: draft } as any);
      toast.success("Respuestas actualizadas");
      setEditing(false);
    } catch (err: any) {
      toast.error(err?.message || "No se pudieron guardar las respuestas");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Respuestas de la Rúbrica LIDER-IA</CardTitle>
          <CardDescription>
            {answered} de {LIDER_IA_COMPONENTS.length} componentes respondidos
          </CardDescription>
        </div>
        {editing ? (
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => { setDraft(answers || {}); setEditing(false); }} disabled={saving}>
              <X className="w-4 h-4 mr-1.5" /> Cancelar
            </Button>
            <Button size="sm" className="gradient-primary" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar
            </Button>
          </div>
        ) : (!readOnly && (
          <Button variant="outline" size="sm" className="shrink-0" onClick={() => { setDraft(answers || {}); setEditing(true); }}>
            <Pencil className="w-4 h-4 mr-1.5" /> Editar
          </Button>
        ))}
      </CardHeader>
      <CardContent className="space-y-5">
        {LIDER_IA_COMPONENTS.map((c) => {
          const value = (editing ? draft[c.id] : answers?.[c.id]) || "";
          return (
            <div key={c.id} className="space-y-2">
              <div className="flex items-center flex-wrap gap-2">
                <Badge variant="outline">{c.dimensionLetter}</Badge>
                <p className="text-sm font-semibold text-foreground">{c.name}</p>
              </div>
              <p className="text-xs text-muted-foreground">{c.question}</p>
              {editing ? (
                <Textarea
                  rows={3}
                  value={value}
                  placeholder={c.placeholder}
                  onChange={(e) => setDraft((p) => ({ ...p, [c.id]: e.target.value }))}
                />
              ) : (
                <p className="text-sm text-muted-foreground bg-muted/40 p-3 rounded-lg whitespace-pre-wrap">
                  {value.trim() || "Sin respuesta"}
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
